/**
 * NAT HOF Analytics Tracker
 * =========================
 * Lightweight analytics pipeline separate from CRM.
 * Sends events to analytics_events table via analytics-ingest edge function.
 * 
 * Standardized event names:
 * - page_view
 * - scroll_depth
 * - quiz_start
 * - quiz_complete
 * - add_to_cart
 * - begin_checkout
 * - purchase_completed
 * - ui_error
 */

import { getSessionId, getExternalId } from '@/lib/crm/track';
import { getAttributionQueryParams } from '@/lib/attribution';

// ─── Medição sempre ativa (sem banner de cookies, decisão do dono 10/2026) ──
export function isAnalyticsAllowed(): boolean {
  return typeof window !== 'undefined';
}


// ─── Buffer & Flush ─────────────────────────────────────────────────────────

const BUFFER_MAX = 10;
const FLUSH_INTERVAL_MS = 5000;

interface AnalyticsEvent {
  event_name: string;
  session_id: string;
  external_id: string;
  path: string;
  source: string;
  props: Record<string, unknown>;
  email?: string;
  phone?: string;
}

// ─── Meta Pixel cookie helpers (for CAPI deduplication) ────────────────────
function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split('=')[1]) : undefined;
}

function generateEventId(): string {
  if (typeof window === 'undefined') {
    return `server_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
  }
  const now = Date.now().toString();
  const rand = Math.random().toString(36).substring(2, 10);
  const path = window.location.pathname.replace(/[^a-z0-9]/gi, '').substring(0, 20);
  return `${now}_${rand}_${path}`;
}

function setGtmEventId(eventId: string): void {
  if (typeof window !== 'undefined') {
    (window as unknown as { __nathof_current_event_id?: string }).__nathof_current_event_id = eventId;
  }
}


let buffer: AnalyticsEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function getEndpoint(): string {
  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  if (projectId) {
    return `https://${projectId}.supabase.co/functions/v1/analytics-ingest`;
  }
  return 'https://ajkvzhvrmnkfuraahtqe.supabase.co/functions/v1/analytics-ingest';
}

async function flush(): Promise<void> {
  if (buffer.length === 0) return;
  
  const batch = [...buffer];
  buffer = [];
  
  try {
    const res = await fetch(getEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(batch),
    });
    
    if (!res.ok) {
      console.warn('[Analytics] Flush failed:', res.status);
      // Re-add to buffer on failure (but don't exceed max)
      buffer = [...batch.slice(0, BUFFER_MAX - buffer.length), ...buffer];
    }
  } catch (err) {
    console.warn('[Analytics] Network error:', err);
  }
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
}

// Flush on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flush();
    }
  });
  window.addEventListener('pagehide', () => flush());
}

// ─── Core Track Function ────────────────────────────────────────────────────

export function analyticsTrack(
  eventName: string,
  props: Record<string, unknown> = {},
  identity: { email?: string; phone?: string } = {},
): void {
  if (typeof window === 'undefined') return;
  if (!isAnalyticsAllowed()) return;


  const enrichedProps: Record<string, unknown> = {
    ...props,
    page_url: (props.page_url as string) || window.location.href,
    event_id: (props.event_id as string) || generateEventId(),
    fbp: (props.fbp as string) || getCookie('_fbp'),
    fbc: (props.fbc as string) || getCookie('_fbc'),
  };

  const event: AnalyticsEvent = {
    event_name: eventName,
    session_id: getSessionId(),
    external_id: getExternalId(),
    path: window.location.pathname,
    source: 'site',
    props: enrichedProps,
    email: identity.email,
    phone: identity.phone,
  };

  buffer.push(event);

  // GTM dataLayer push (complementa CAPI server-side)
  // NOTA: view_pdp NÃO está aqui — o push view_item é feito explicitamente
  // via dlViewItem() no PDP com schema ecommerce completo. Evita duplicate event.
  // page_view também é enviado explicitamente por usePageTracking via dlPageView().
  const dlEventMap: Record<string, string> = {
    lead: 'generate_lead',
    quiz_complete: 'generate_lead',
    view_item_list: 'view_item_list',
  };
  const dlEvent = dlEventMap[eventName];
  if (dlEvent) {
    setGtmEventId(enrichedProps.event_id as string);
    pushDataLayer(dlEvent, {
      ...enrichedProps,
      currency: (enrichedProps.currency as string) || 'BRL',
    });
  }

  // Pinterest event push
  const pinEventMap: Record<string, string> = {
    view_pdp: 'pagevisit',
    view_item: 'pagevisit',
    add_to_cart: 'addtocart',
    begin_checkout: 'checkout',
    purchase: 'checkout',
    purchase_completed: 'checkout',
    lead: 'lead',
  };
  const pinEvent = pinEventMap[eventName];
  if (pinEvent) {
    pushPinterestEvent(pinEvent, {
      value: (enrichedProps.value as number) || 0,
      currency: 'BRL',
      order_quantity: (enrichedProps.num_items as number) || 1,
      line_items: enrichedProps.content_ids
        ? [{ product_id: (enrichedProps.content_ids as string[])[0], product_price: enrichedProps.value }]
        : [],
    });
  }

  if (buffer.length >= BUFFER_MAX) {
    flush();
  } else {
    scheduleFlush();
  }
}

// ─── GTM dataLayer + Pinterest helpers ─────────────────────────────────────

function pushDataLayer(event: string, data: Record<string, unknown> = {}): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as { dataLayer?: unknown[] };
  if (Array.isArray(w.dataLayer)) {
    w.dataLayer.push({ event, ...data });
  }
}

function pushPinterestEvent(event: string, data: Record<string, unknown> = {}): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as { pintrk?: (...args: unknown[]) => void };
  if (typeof w.pintrk === 'function') {
    w.pintrk('track', event, data);
  }
}

// ─── Cross-domain GA4 linker (Shopify checkout) ────────────────────────────
// GA4 Measurement ID — usado pra recuperar client_id/session_id via gtag('get').
// Mantenha sincronizado com o ID configurado no GTM container GTM-KGGTZRWN.
const GA4_MEASUREMENT_ID = 'G-2SJF6ZCKGY';

/**
 * Decora URL do checkout (cross-domain) com identificadores GA4.
 * Estratégia (com fallback em cascata):
 *  1. Tenta gtag('get', MID, 'client_id') + 'session_id' (GA4 nativo, async, 250ms timeout)
 *  2. Fallback: cookie _ga (legacy, ainda funciona p/ stitching)
 *  3. Última opção: URL original (cookieless ping vai depender do url_passthrough)
 * IMPORTANTE: o linker `_gl` oficial é hash-assinado e só pode ser gerado pelo
 * próprio gtag.js. A solução definitiva é habilitar "Configure Linker" no GTM
 * container apontando para checkout.nathof.com.br. Esta função é o melhor esforço client-side.
 */
export async function decorateCheckoutUrl(url: string): Promise<string> {
  if (typeof window === 'undefined') return url;
  const extra = new URLSearchParams();
  try {
    const w = window as unknown as {
      gtag?: (...args: unknown[]) => void;
    };

    // 1. GA4 cross-domain: client_id + session_id via gtag
    if (typeof w.gtag === 'function') {
      const getField = (field: string) =>
        new Promise<string | undefined>((resolve) => {
          const timeout = setTimeout(() => resolve(undefined), 250);
          try {
            w.gtag!('get', GA4_MEASUREMENT_ID, field, (val: string) => {
              clearTimeout(timeout);
              resolve(val);
            });
          } catch {
            clearTimeout(timeout);
            resolve(undefined);
          }
        });

      const [clientId, sessionId] = await Promise.all([
        getField('client_id'),
        getField('session_id'),
      ]);

      if (clientId) {
        extra.set('_ga_cid', clientId);
        if (sessionId) extra.set('_ga_sid', sessionId);
      }
    }

    // 2. Fallback: cookie _ga (formato GA1.1.<client_id>.<timestamp>)
    if (!extra.has('_ga_cid')) {
      const gaMatch = document.cookie.match(/_ga=([^;]+)/);
      if (gaMatch) extra.set('_ga', gaMatch[1]);
    }
  } catch { /* silent — cookieless ping via url_passthrough cuidará da atribuição */ }

  // 3. Propagate first-touch UTMs + gclid/fbclid so paid attribution survives the
  // storefront -> checkout.nathof.com.br hop (complements the cart attributes).
  try {
    const attribution = getAttributionQueryParams();
    Object.entries(attribution).forEach(([k, v]) => {
      if (!extra.has(k)) extra.set(k, v);
    });
  } catch { /* non-blocking */ }

  const qs = extra.toString();
  if (!qs) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}${qs}`;
}

// ─── Convenience Functions ──────────────────────────────────────────────────

export function trackPageView(path?: string): void {
  analyticsTrack('page_view', {
    referrer: document.referrer || null,
    title: document.title,
    url: window.location.href,
    path: path || window.location.pathname,
  });
}

export function trackScrollDepth(depthPct: number, route: string): void {
  analyticsTrack('scroll_depth', {
    depth_pct: depthPct,
    route,
  });
}

export function trackQuizStart(): void {
  analyticsTrack('quiz_start', {});
}

export function trackQuizComplete(data: {
  skinType?: string;
  productsCount?: number;
}): void {
  analyticsTrack('quiz_complete', data);
}

export function trackAddToCart(data: {
  sku?: string;
  productName?: string;
  price?: number;
  handle?: string;
}): void {
  analyticsTrack('add_to_cart', {
    ...data,
    product_handle: data.handle,
    handle: data.handle,
  });
}

export function trackBeginCheckout(data?: {
  cartTotal?: number;
  itemCount?: number;
}): void {
  analyticsTrack('begin_checkout', data || {});
}

/**
 * Fire-and-forget begin_checkout beacon to analytics-ingest.
 * Uses fetch keepalive so the request survives the immediate redirect to Shopify checkout.
 * Does NOT go through the internal buffer — bypasses the FLUSH_INTERVAL to guarantee delivery
 * even when the page is unloading.
 */
export function sendBeginCheckoutBeacon(data: {
  items: Array<{ sku?: string; quantity: number; price: number }>;
  value: number;
}): void {
  if (typeof window === 'undefined') return;
  if (!isAnalyticsAllowed()) return;

  try {
    const props: Record<string, unknown> = {
      event_id: generateEventId(),
      page_url: window.location.href,
      items: data.items,
      value: data.value,
      currency: 'BRL',
      num_items: data.items.reduce((s, i) => s + (i.quantity || 0), 0),
      fbp: getCookie('_fbp'),
      fbc: getCookie('_fbc'),
    };
    const payload: AnalyticsEvent = {
      event_name: 'begin_checkout',
      session_id: getSessionId(),
      external_id: getExternalId(),
      path: window.location.pathname,
      source: 'site',
      props,
    };
    const body = JSON.stringify([payload]);
    // keepalive ensures the POST survives the imminent navigation to Shopify.
    fetch(getEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => { /* fire-and-forget */ });
  } catch { /* non-blocking */ }
}

export function trackPurchaseCompleted(data?: {
  orderId?: string;
  orderTotal?: number;
  total_price?: number;
}): void {
  const { orderId, orderTotal, total_price, ...rest } = data || {};
  analyticsTrack('purchase_completed', {
    order_id: orderId,
    total_price: total_price ?? orderTotal ?? 0,
    ...rest,
  });
}

export function trackUiError(data: {
  message: string;
  component?: string;
  route?: string;
}): void {
  analyticsTrack('ui_error', data);
}

// ─── PDP Enhanced Events ──────────────────────────────────────────────────

export function trackVariantSelect(data: {
  handle: string;
  variantId: string;
  variantTitle: string;
  price: number;
}): void {
  analyticsTrack('variant_select', {
    handle: data.handle,
    product_handle: data.handle,
    variant_id: data.variantId,
    variant_title: data.variantTitle,
    price: data.price,
  });
}

export function trackImageZoom(handle: string, imageIndex: number): void {
  analyticsTrack('image_zoom', { handle, product_handle: handle, image_index: imageIndex });
}

export function trackFaqOpen(handle: string, question: string): void {
  analyticsTrack('faq_open', { handle, product_handle: handle, question });
}

export function trackPdpTimeOnPage(handle: string, seconds: number): void {
  analyticsTrack('pdp_time_on_page', { handle, product_handle: handle, seconds });
}

export function trackReviewsOpen(handle: string): void {
  analyticsTrack('reviews_open', { handle, product_handle: handle });
}

export function trackShippingCalculator(handle: string): void {
  analyticsTrack('shipping_calculator_used', { handle, product_handle: handle });
}

// ─── Navigation Events ────────────────────────────────────────────────────

export function trackViewCollection(collectionName: string): void {
  analyticsTrack('view_collection', { collection_name: collectionName });
}

export function trackInternalSearch(query: string, resultCount: number): void {
  analyticsTrack('internal_search', { query, result_count: resultCount });
}

export function trackSearchResultClick(query: string, handle: string, position: number): void {
  analyticsTrack('search_result_click', { query, handle, product_handle: handle, position });
}

export function trackMenuOpen(variant: 'mobile' | 'desktop'): void {
  analyticsTrack('menu_open', { variant });
}

export function trackReturnVisit(): void {
  if (typeof window === 'undefined') return;
  const key = 'nathof_visited';
  const wasVisited = localStorage.getItem(key);
  if (wasVisited) {
    analyticsTrack('return_visit', { previous_visit: wasVisited });
  }
  localStorage.setItem(key, new Date().toISOString());
}

// ─── Cart Events ──────────────────────────────────────────────────────────

export function trackRemoveItem(data: { sku: string; handle: string; price: number }): void {
  analyticsTrack('remove_item', {
    sku: data.sku,
    handle: data.handle,
    product_handle: data.handle,
    price: data.price,
  });
}

export function trackCartQuantityChange(data: {
  sku: string;
  handle: string;
  direction: 'increase' | 'decrease';
  newQty: number;
}): void {
  analyticsTrack('cart_quantity_change', { ...data, product_handle: data.handle });
}

// ─── Scroll Milestones ────────────────────────────────────────────────────

export function trackScrollMilestone(pct: 25 | 50 | 75 | 90, route: string): void {
  analyticsTrack(`scroll_${pct}`, { depth_pct: pct, route });
}

// ─── UX Events ───────────────────────────────────────────────────────────

export function trackStickyCTAClick(location: string): void {
  analyticsTrack('sticky_cta_click', { location });
}

export function trackBannerClick(bannerId: string, destination: string): void {
  analyticsTrack('banner_click', { banner_id: bannerId, destination });
}

export function trackJSError(data: { message: string; source?: string; lineno?: number }): void {
  analyticsTrack('js_error', data);
}

// ─── Auto-init: JS error capture ─────────────────────────────────────────

// Third-party script domains we must NEVER log as our own errors. These produce
// cross-origin "Script error." noise and false alerts (e.g. "JS errors block the
// buy button") even though the site works fine.
const THIRD_PARTY_ERROR_DOMAINS = [
  'connect.facebook.net',
  'facebook.net',
  's.pinimg.com',
  'pinimg.com',
  'google-analytics.com',
  'googletagmanager.com',
  'doubleclick',
  'google.com',
  'tiktok',
  'snap',
];

// Known third-party error message signatures. These arrive with OUR filename
// (browser extensions / embedded webviews inject scripts into our page), so the
// domain list alone can't catch them.
const THIRD_PARTY_ERROR_SIGNATURES = [
  'googletag is not defined',
  'window.webkit.messageHandlers',
  'webkit.messageHandlers',
  'Error invoking postMessage',
  'Java object is gone',
  'Java exception was raised',
  'Script error',
];

function isThirdPartySignature(message: string): boolean {
  return THIRD_PARTY_ERROR_SIGNATURES.some((sig) =>
    message.toLowerCase().includes(sig.toLowerCase()),
  );
}

/**
 * Classifies an error:
 *  - 'third_party' → drop (known noise: extensions, webviews, ad scripts)
 *  - 'unknown'     → log with origin marker (no filename = can't attribute)
 *  - 'own'         → real error from our bundle
 */
function classifyError(
  filename: string | undefined,
  message: string | undefined,
): 'own' | 'unknown' | 'third_party' {
  if (!message) return 'third_party';
  if (isThirdPartySignature(message)) return 'third_party';

  // No filename → almost always an injected/extension script. Keep it in the
  // database but marked so it doesn't pollute the application error count.
  if (!filename) return 'unknown';

  if (THIRD_PARTY_ERROR_DOMAINS.some((domain) => filename.includes(domain))) {
    return 'third_party';
  }

  return 'own';
}

// Per-session cap: one broken extension used to write hundreds of rows.
const JS_ERROR_SESSION_LIMIT = 5;
let jsErrorCount = 0;

if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    const origin = classifyError(event.filename, event.message);
    if (origin === 'third_party') return;
    if (jsErrorCount >= JS_ERROR_SESSION_LIMIT) return;
    jsErrorCount += 1;

    analyticsTrack('js_error', {
      message: event.message,
      source: event.filename,
      lineno: event.lineno,
      origin,
    });
  });
}

